import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { type TokenResult } from '@square/web-payments-sdk-types';
import Card from 'typescript/apps/checkout_v3/components/payment/card';
import PaymentMethods from 'typescript/apps/checkout_v3/components/payment/payment_methods';
import SubmitButton from 'typescript/apps/checkout_v3/components/payment/submit_button';
import SavedCardsOptions from 'typescript/apps/checkout_v3/components/payment/saved_cards/saved_cards_options';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useSavedCardsContext } from 'typescript/context/saved_cards';
import {
  usePaymentForm,
  usePaymentSdk,
} from 'typescript/utilities/web_payment_sdk';
import PageLoader from 'typescript/components/page_loader';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useSelectPaymentOption } from 'typescript/hooks/use_payment_options_client';
import {
  orderHasNoSoftwareProducts,
  orderIsPayInFull,
  orderIsWarranty,
  userIsLoggedIn,
} from 'typescript/utilities/order_data';
import { usePaymentContext } from 'typescript/context/payment_context';
import {
  PaymentMethod,
  useSubmitOrder,
} from 'typescript/apps/checkout_v3/components/payment/use_submit_order';
import {
  PaymentSchedule,
  PaymentType,
} from 'typescript/constants/enums/payment';
import useAddressForm from 'typescript/apps/checkout_v3/components/address_form/use_address_form';
import { AddressType } from 'typescript/schemas/address_forms/address_fields';
import { useLocaleInfo } from 'typescript/context/locale_info';
import BillingAddress from 'typescript/apps/checkout_v3/components/payment/billing_address';
import { buildBillingAddress } from 'typescript/utilities/address_utils';
import { useIsProcessingAnyRequest } from 'typescript/hooks/use_react_query_utils';
import Disclaimer from 'typescript/apps/checkout_v3/components/payment/disclaimer';
import BundleReconciliationMessage from 'typescript/apps/checkout_v3/components/payment/bundle_reconciliation_message';
import {
  CHECKOUT_PAGE_CLICK_EVENTS,
  COMPONENT_NAMES,
} from 'typescript/constants/tracking';
import {
  onCheckoutPageClick,
  onCheckoutPageViewCombined,
} from 'typescript/utilities/tracking';

const PayInFull = ({ className = '' }) => {
  const { t } = useTranslation();

  const { countryCode } = useLocaleInfo();
  const { orderNumber, trackingData } = useCheckoutFlow();

  const { submittingPayment, sameBillingAsShipping } = usePaymentContext();

  const { isProcessing } = useIsProcessingAnyRequest();

  const {
    makeDefaultCard,
    setMakeDefaultCard,
    savedCardsEnabled,
    saveCardToWallet,
    setSaveCardToWallet,
    selectedCard,
  } = useSavedCardsContext();

  const { formMethods: addressFormMethods } = useAddressForm({
    countryCode,
    addressType: AddressType.billing,
  });
  const {
    formState: { isValid: isAddressFormValid },
    getValues: getFormValues,
  } = addressFormMethods;

  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    // error: orderDataError, // TODO: handle error state
  } = useGetOrderSummary({ orderNumber });

  const { selectPaymentOption } = useSelectPaymentOption({ orderNumber });

  const scriptLoaded = usePaymentSdk();
  const { sqPayments, sqCard, googlePay, applePay, cashAppPay } =
    usePaymentForm(scriptLoaded, orderSummaryData);

  const { submitOrder } = useSubmitOrder(orderNumber);

  if (orderDataIsPending) {
    return <PageLoader />;
  }

  const currentUserIsLoggedIn = userIsLoggedIn(orderSummaryData?.current_user);

  const canUseSavedCard =
    savedCardsEnabled &&
    currentUserIsLoggedIn &&
    orderHasNoSoftwareProducts(orderSummaryData?.software_summary);

  const handleSubmit = async (
    paymentMethod: PaymentMethod,
    paymentType: PaymentType,
    cashAppTokenResult: TokenResult | undefined = undefined,
  ) => {
    // If not card payment, we get billing address from Web Payment SDK.
    const billingAddress =
      paymentType === PaymentType.CARD
        ? buildBillingAddress({
            sameBillingAsShipping,
            billingAddressFormValues: getFormValues(),
            shippingAddress: orderSummaryData?.shipping_address,
            contactInformation: orderSummaryData?.contact_information,
          })
        : undefined;

    await submitOrder({
      paymentMethod,
      paymentType,
      sqPayments: sqPayments.current,
      billingAddress,
      cashAppTokenResult,
    });
  };

  useEffect(() => {
    if (orderSummaryData && !orderIsPayInFull(orderSummaryData?.payment_due)) {
      selectPaymentOption({ paymentOption: PaymentSchedule.UPFRONT });
    }
  }, [orderSummaryData]);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.PAY_IN_FULL,
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <FormProvider {...addressFormMethods}>
      <form className={`shop-grid-container ${className}`}>
        <Card
          className="shop-grid-item-full"
          sqCard={sqCard}
          canUseSavedCard={canUseSavedCard}
        />
        <SavedCardsOptions
          className="shop-grid-item-full"
          userIsLoggedIn={currentUserIsLoggedIn}
          orderIsWarranty={orderIsWarranty(orderSummaryData)}
          savedCardsEnabled={savedCardsEnabled}
          selectedCard={selectedCard}
          saveCardToWallet={saveCardToWallet}
          setSaveCardToWallet={setSaveCardToWallet}
          makeDefaultCard={makeDefaultCard}
          setMakeDefaultCard={setMakeDefaultCard}
        />
        <BillingAddress className="shop-grid-item-full" />
        <PaymentMethods
          className="shop-grid-item-full"
          submit={handleSubmit}
          disabled={submittingPayment}
          applePay={applePay}
          googlePay={googlePay}
          cashAppPay={cashAppPay}
        />
        <Disclaimer className="shop-grid-item-full" />
        <BundleReconciliationMessage className="shop-grid-item-full" />
        <SubmitButton
          className="shop-grid-item-full"
          disabled={
            isProcessing ||
            submittingPayment ||
            !sqCard ||
            (!sameBillingAsShipping && !isAddressFormValid)
          }
          buttonCTA={t('checkout.payment_v3.payNow')}
          onClick={() => {
            onCheckoutPageClick({
              eventDesc:
                CHECKOUT_PAGE_CLICK_EVENTS.payment_page_cta_pay_in_full,
              trackingData,
            });
            return handleSubmit(sqCard, PaymentType.CARD);
          }}
        />
        <div id="hidden-div-for-gpay-test" style={{ display: 'none' }} />
      </form>
    </FormProvider>
  );
};

export default PayInFull;
